import { useCallback, useEffect, useRef, useState } from 'react';

const testimonials = [
  {
    id: 1,
    quote:
      'BPS is an integral part of our team and provides critical support which allows us to expedite timelines, turn around key deal analysis quickly and deliver high quality work products. BPS has a world class team with a great work ethic. The team’s dedication to perfection enhances the quality of our deliverables and gives us the confidence that analyses are completed with high integrity.',
    role: 'Managing Director',
    company: 'Growing Consulting Firm',
  },
  {
    id: 2,
    quote:
      'As an early-stage growth company, our finance function has to stay nimble with the entrepreneurial vision of our company founder. For the last three years, BPS has proven to be a reliable partner with an inclination to contribute to our business\'s ever-changing demands. Additionally, we recently had employee turnover on our finance team and BPS was critical to the continuity of the finance function. Their team has consistently demonstrated the highest degree of responsiveness while delivering the highest quality work. I consider the BPS team to be an indispensable partner to our firm.',
    role: 'CFO',
    company: 'Healthcare/Pharmacy Business Firm, Midwest US',
  },
  {
    id: 3,
    quote:
      'I consider BPS an integral part of my deal team. They enable us to increase leverage and accelerate work by orders of magnitude greater than what our onshore team can accomplish alone. Having worked with the team for more than three years and across several firms, I cannot recommend them highly enough.',
    role: 'Director - M&A',
    company: 'Healthcare Advisory Firm, US',
  },
];

const AUTO_MS = 4000;

function Stars() {
  return (
    <div className="flex shrink-0 items-center gap-0.5" aria-label="5 star rating">
      {Array.from({ length: 5 }).map((_, index) => (
        <svg
          key={index}
          viewBox="0 0 20 20"
          fill="#F5B400"
          className="h-4 w-4"
          aria-hidden="true"
        >
          <path d="M10 1.5l2.47 5.01 5.53.8-4 3.9.94 5.5L10 14.9l-4.94 2.61.94-5.5-4-3.9 5.53-.8L10 1.5z" />
        </svg>
      ))}
    </div>
  );
}

function TestimonialCard({ quote, role, company }) {
  const [expanded, setExpanded] = useState(false);
  const previewLength = 160;
  const needsToggle = quote.length > previewLength;
  const displayText =
    expanded || !needsToggle ? quote : `${quote.slice(0, previewLength).trim()}...`;

  return (
    <article className="flex h-full flex-col rounded-2xl border border-brand-border bg-brand-white p-5 shadow-[0_12px_40px_-24px_rgba(17,22,44,0.35)] sm:p-7">
      <p className="flex-1 text-[14px] leading-relaxed text-brand-navy sm:text-body-sm">
        {displayText}
      </p>

      {needsToggle ? (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-3 self-start text-body-sm font-semibold text-brand-primary transition-colors hover:text-brand-dark"
        >
          {expanded ? 'Show Less' : 'Show More'}
        </button>
      ) : null}

      <div className="mt-6 flex items-end justify-between gap-3 border-t border-brand-border/70 pt-4">
        <div className="min-w-0">
          <p className="text-body-sm font-bold text-brand-navy">{role}</p>
          <p className="mt-0.5 text-label text-brand-secondary">{company}</p>
        </div>
        <Stars />
      </div>
    </article>
  );
}

function useVisibleCount() {
  const [visible, setVisible] = useState(3);

  useEffect(() => {
    const update = () => {
      if (window.matchMedia('(min-width: 1280px)').matches) {
        setVisible(3);
      } else if (window.matchMedia('(min-width: 768px)').matches) {
        setVisible(2);
      } else {
        setVisible(1);
      }
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return visible;
}

function TestimonialsSlider({ items }) {
  const visibleCount = useVisibleCount();
  const count = items.length;
  // Duplicate once so we can loop without a visible jump
  const loopItems = [...items, ...items];
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const hovering = useRef(false);
  const resetting = useRef(false);

  const goNext = useCallback(() => {
    if (resetting.current) return;
    setAnimate(true);
    setIndex((current) => current + 1);
  }, []);

  const goPrev = useCallback(() => {
    if (resetting.current) return;
    if (index === 0) {
      // Jump to the mirrored set without animation, then step back
      resetting.current = true;
      setAnimate(false);
      setIndex(count);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimate(true);
          setIndex(count - 1);
          resetting.current = false;
        });
      });
      return;
    }
    setAnimate(true);
    setIndex((current) => current - 1);
  }, [count, index]);

  // Seamless wrap: when we land on the duplicated first slide, snap back
  useEffect(() => {
    if (index < count) return undefined;

    const timer = window.setTimeout(() => {
      resetting.current = true;
      setAnimate(false);
      setIndex(0);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimate(true);
          resetting.current = false;
        });
      });
    }, 500);

    return () => window.clearTimeout(timer);
  }, [index, count]);

  // Keep index valid when breakpoint changes
  useEffect(() => {
    setIndex((current) => current % count);
  }, [visibleCount, count]);

  // Auto-rotate
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!hovering.current && !resetting.current) goNext();
    }, AUTO_MS);

    return () => window.clearInterval(timer);
  }, [goNext]);

  const activeDot = index % count;

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        hovering.current = true;
      }}
      onMouseLeave={() => {
        hovering.current = false;
      }}
    >
      <div className="-mx-3 overflow-hidden">
        <div
          className={`flex ${animate ? 'transition-transform duration-500 ease-out' : ''}`}
          style={{
            transform: `translateX(-${(index * 100) / visibleCount}%)`,
          }}
        >
          {loopItems.map((item, slideIndex) => (
            <div
              key={`${item.id}-${slideIndex}`}
              className="min-w-0 shrink-0 px-3"
              style={{ width: `${100 / visibleCount}%` }}
            >
              <TestimonialCard {...item} />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={goPrev}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-border bg-brand-white text-brand-navy transition-colors hover:border-brand-primary hover:text-brand-primary"
          aria-label="Previous testimonials"
        >
          <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
            <path
              d="M15 6l-6 6 6 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="flex items-center gap-2" role="tablist" aria-label="Testimonial slides">
          {items.map((item, dotIndex) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={activeDot === dotIndex}
              onClick={() => {
                setAnimate(true);
                setIndex(dotIndex);
              }}
              className={`h-2.5 rounded-full transition-all ${
                activeDot === dotIndex
                  ? 'w-6 bg-brand-primary'
                  : 'w-2.5 bg-brand-border hover:bg-brand-muted'
              }`}
              aria-label={`Go to slide ${dotIndex + 1}`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={goNext}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-border bg-brand-white text-brand-navy transition-colors hover:border-brand-primary hover:text-brand-primary"
          aria-label="Next testimonials"
        >
          <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
            <path
              d="M9 6l6 6-6 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

function Testimonials() {
  return (
    <section id="testimonials" className="bg-brand-background py-6 lg:py-10">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="mx-auto mb-10 max-w-3xl text-center lg:mb-14">
          <p className="mb-3 text-label font-semibold uppercase tracking-[0.2em] text-brand-primary">
            Trusted By Industry
          </p>
          <h2 className="text-section uppercase tracking-wide text-brand-navy sm:text-[36px]">
            What Industry Partners Say About Us
          </h2>
          <div className="mx-auto mt-5 h-1 w-16 rounded-full bg-brand-primary" />
        </div>

        <TestimonialsSlider items={testimonials} />
      </div>
    </section>
  );
}

export default Testimonials;
